import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { Add, Edit, Delete, Visibility } from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useNewsItems } from '../hooks/useNewsItems';
import { NewsItem } from '../types';

interface NewsFormData {
  title: string;
  category: 'UPDATE' | 'EVENT' | 'MAINTENANCE' | 'ANNOUNCEMENT';
  content: string;
  isPublished: boolean;
  order: number;
}

const initialFormData: NewsFormData = {
  title: '',
  category: 'ANNOUNCEMENT',
  content: '',
  isPublished: false,
  order: 0,
};

const categoryColors: Record<string, 'primary' | 'secondary' | 'warning' | 'info'> = {
  'UPDATE': 'primary',
  'EVENT': 'secondary',
  'MAINTENANCE': 'warning',
  'ANNOUNCEMENT': 'info',
};

const categoryLabels: Record<string, string> = {
  'UPDATE': 'アップデート',
  'EVENT': 'イベント', 
  'MAINTENANCE': 'メンテナンス',
  'ANNOUNCEMENT': 'お知らせ',
};

export const NewsManagement: React.FC = () => {
  const { newsItems, loading, error, createNewsItem, updateNewsItem, deleteNewsItem } = useNewsItems();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);
  const [previewNews, setPreviewNews] = useState<NewsItem | null>(null);
  const [formData, setFormData] = useState<NewsFormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);

  const handleOpenDialog = (news?: NewsItem) => {
    if (news) {
      setEditingNews(news);
      setFormData({
        title: news.title,
        category: news.category,
        content: news.content || '',
        isPublished: news.isPublished,
        order: news.order,
      });
    } else {
      setEditingNews(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingNews(null);
    setFormData(initialFormData);
  };

  const handleOpenPreview = (news: NewsItem) => {
    setPreviewNews(news);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewNews(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const newsData = {
        ...formData,
        date: new Date().toISOString(),
      };

      if (editingNews) {
        await updateNewsItem(editingNews.id, newsData);
      } else {
        await createNewsItem(newsData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save news:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('このお知らせを削除しますか？')) {
      try {
        await deleteNewsItem(id);
      } catch (error) {
        console.error('Failed to delete news:', error);
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'title', headerName: 'タイトル', width: 300 },
    {
      field: 'category',
      headerName: 'カテゴリ',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={categoryLabels[params.value] || params.value}
          color={categoryColors[params.value] || 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'isPublished',
      headerName: '公開状態',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? '公開中' : '下書き'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    { field: 'order', headerName: '順序', width: 80 },
    {
      field: 'date',
      headerName: '作成日',
      width: 150,
      renderCell: (params) => new Date(params.value).toLocaleDateString('ja-JP'),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '操作',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<Visibility />}
          label="プレビュー"
          onClick={() => handleOpenPreview(params.row)}
        />,
        <GridActionsCellItem
          icon={<Edit />}
          label="編集"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<Delete />}
          label="削除"
          onClick={() => handleDelete(params.id as string)}
        />,
      ],
    },
  ];

  if (loading && newsItems.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          お知らせ管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          新規作成
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={newsItems}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
        />
      </Box>

      {/* 編集/新規作成ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNews ? 'お知らせを編集' : '新しいお知らせを作成'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="タイトル"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            margin="normal"
            required
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>カテゴリ</InputLabel>
            <Select
              value={formData.category}
              label="カテゴリ"
              onChange={(e) => setFormData({ ...formData, category: e.target.value as typeof formData.category })}
            >
              <MenuItem value="ANNOUNCEMENT">お知らせ</MenuItem>
              <MenuItem value="UPDATE">アップデート</MenuItem>
              <MenuItem value="EVENT">イベント</MenuItem>
              <MenuItem value="MAINTENANCE">メンテナンス</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="内容"
            multiline
            rows={6}
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="表示順序"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isPublished}
                onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
              />
            }
            label="公開する"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            キャンセル
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.title}
          >
            {submitting ? <CircularProgress size={20} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* プレビューダイアログ */}
      <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle>
          プレビュー
        </DialogTitle>
        <DialogContent>
          {previewNews && (
            <Paper sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={categoryLabels[previewNews.category] || previewNews.category}
                  color={categoryColors[previewNews.category] || 'default'}
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="h5" component="h2" gutterBottom>
                  {previewNews.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {new Date(previewNews.date).toLocaleDateString('ja-JP')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {previewNews.content}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};